import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import type {
  EntityVote,
  EntityVoteDetails,
  EntityType,
} from '@/features/votes/entity-votes';
import type {
  AnnotationNote,
  Annotation,
} from '@/features/votes/message-votes';

interface EntityVotesResponse {
  votes: EntityVote[];
  total: number;
}

export type VoteFilter = 'all' | 'upvote' | 'downvote';

function voteFilterParams(
  entityType: EntityType | null,
  filter: VoteFilter,
  search: string
): URLSearchParams {
  const params = new URLSearchParams({ filter });
  if (entityType) {
    params.append('entity_type', entityType);
  }
  if (search) {
    params.append('search', search);
  }
  return params;
}

export function useEntityVotes(
  page: number = 1,
  limit: number = 50,
  entityType: EntityType | null = null,
  filter: VoteFilter = 'all',
  search: string = ''
) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['entity-votes', page, limit, entityType, filter, search],
    queryFn: async (): Promise<EntityVotesResponse> => {
      const token = await getAccessTokenSilently();

      const params = voteFilterParams(entityType, filter, search);
      params.set('page', page.toString());
      params.set('limit', limit.toString());

      const response = await apiFetch(
        `${API_URL}/admin/entity-votes?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message || 'Failed to fetch entity votes'
        );
      }

      const votes = await response.json();

      // Also fetch total count
      const countParams = voteFilterParams(entityType, filter, search);

      const countResponse = await apiFetch(
        `${API_URL}/admin/entity-votes/count?${countParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const countData = await countResponse.json();

      return {
        votes,
        total: countData.total_count || 0,
      };
    },
    enabled: true,
  });
}

interface EntityVotesExport {
  entityType: EntityType | null;
  filter: VoteFilter;
  search: string;
}

/** Downloads the newest votes the filters match, up to the backend's cap, not only the page on screen. */
export function useExportEntityVotes() {
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async ({ entityType, filter, search }: EntityVotesExport) => {
      const token = await getAccessTokenSilently();
      const params = voteFilterParams(entityType, filter, search);
      const response = await apiFetch(
        `${API_URL}/admin/entity-votes/export?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        throw new Error('Failed to export entity votes');
      }

      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = `entity-votes-${entityType ?? 'all'}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    },
  });
}

export function useEntityVoteDetails(voteId: number | null) {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery({
    queryKey: ['entity-vote-details', voteId],
    queryFn: async (): Promise<EntityVoteDetails> => {
      if (!voteId) throw new Error('Vote ID is required');

      const token = await getAccessTokenSilently();

      const response = await apiFetch(
        `${API_URL}/admin/entity-votes/${voteId}/details`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message ||
            'Failed to fetch entity vote details'
        );
      }

      return await response.json();
    },
    enabled: !!voteId,
  });
}

interface UpdateEntityAnnotationRequest {
  entity_type: string;
  entity_id: number;
  status?: string | null;
  tags?: string[];
  notes?: AnnotationNote[];
}

export function useUpdateEntityAnnotation(voteId: number) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: UpdateEntityAnnotationRequest
    ): Promise<Annotation> => {
      const token = await getAccessTokenSilently();

      const response = await apiFetch(`${API_URL}/admin/annotations`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message || 'Failed to update annotation'
        );
      }

      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['entity-vote-details', voteId],
      });
    },
  });
}
